import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { businessUnitsApi } from '@/lib/api';
import { unitErrorMessage, unitLabel, useVisibleUnits } from '@/lib/business-units';
import { queryKeys } from '@/lib/query-keys';
import { UnitRoleSelect } from './unit-role-select';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The employee being granted access. */
  user: { id: string; name: string };
  /** Units not to offer — the ones the employee already reaches. */
  excludeUnitIds: readonly string[];
}

/**
 * Grant an employee access to a business unit other than their primary
 * employer, with one of THAT unit's roles. One grant per submit: access is
 * changed by explicit operations, never by saving a list. A Super Admin's,
 * from the employee's page.
 */
export function GrantUnitAccessDialog({ open, onOpenChange, user, excludeUnitIds }: Props) {
  const businessUnits = useVisibleUnits();
  const queryClient = useQueryClient();
  const [unitId, setUnitId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUnitId(null);
    setRoleId(null);
    setError(null);
  }, [open]);

  const grant = useMutation({
    mutationFn: () =>
      businessUnitsApi.grant(unitId as string, {
        userId: user.id,
        systemRoleId: roleId as string,
      }),
    onSuccess: () => {
      toast.success('Access granted');
      queryClient.invalidateQueries({ queryKey: queryKeys.businessUnits.all });
      onOpenChange(false);
    },
    onError: (err) => setError(unitErrorMessage(err, 'Could not grant access.')),
  });

  const units = businessUnits.filter((u) => !excludeUnitIds.includes(u.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Grant access to a Business Unit</DialogTitle>
          <DialogDescription>
            {user.name} keeps their primary employer, and can also work in this unit — under the
            role you choose, one of its own.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Business Unit</Label>
            {units.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                They already work in every Business Unit.
              </p>
            ) : (
              <Select
                value={unitId ?? undefined}
                onValueChange={(v) => {
                  setUnitId(v);
                  setRoleId(null);
                }}
              >
                <SelectTrigger className="w-full" aria-label="Business Unit">
                  <SelectValue placeholder="Choose a Business Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {unitLabel(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Role in that unit</Label>
            <UnitRoleSelect businessUnitId={unitId} value={roleId} onChange={setRoleId} />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => grant.mutate()} disabled={!unitId || !roleId || grant.isPending}>
            {grant.isPending ? 'Granting…' : 'Grant access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
